import React, { FC, lazy, Suspense } from 'react';
import { ReactComponent as MetamaskIcon } from '@blink/assets/src/icons/metamask.svg';
import { ReactComponent as RabbyIcon } from '@blink/assets/src/icons/rabby.svg';
import { ReactComponent as WalletConnectIcon } from '@blink/assets/src/icons/walletConnect.svg';
import { WALLET } from '@blink/components/src/types';
import { INSTALL_METAMASK_URL } from '@blink/components/src/constants/global';

import { useHideModal } from '../../../../../../components/Modals/ModalWrapperHooks';
import { useWalletSelection } from '../hooks/useWalletSelection';
import { useTermsAcceptance } from '../hooks/useTermsAcceptance';
import { useHandleConnectWallet } from '../hooks/useHandleConnectWallet';

import { SelectableCard } from '@/widgets/SelectableCard';
import { PATHS } from '@/routes/paths';
import { Button, ButtonSize, ButtonVariant, Checkbox } from '@/shared/ui';
import useLocalFeatureFlag from '@/shared/hooks/useLocalFeatureFlag';
import { Features } from '@/shared/configs/features';

const LazyConnectWalletModal = lazy(() =>
    import('./v2/ConnectWalletModal').then((module) => ({
        default: module.ConnectWalletModal,
    })),
);

export const BaseConnectWalletModal: FC = () => {
    const { handleCloseModal } = useHideModal();

    const { selectedWallet, handleMetamaskSelect, handleRabbySelect, handleWalletConnectSelect } =
        useWalletSelection();
    const { termsAccepted, handleTermsAccept } = useTermsAcceptance();
    const handleConnect = useHandleConnectWallet(selectedWallet, handleCloseModal);

    return (
        <div className='flex flex-col'>
            <div className='text-[16px] my-7 mb-5 font-semibold font-[Inter]'>Choose Wallet</div>
            <div className='flex gap-4 min-w-[430px]'>
                <SelectableCard
                    isActive={selectedWallet === WALLET.META_MASK}
                    icon={<MetamaskIcon />}
                    name='Metamask'
                    onClick={handleMetamaskSelect}
                />
                <SelectableCard
                    isActive={selectedWallet === WALLET.WALLET_CONNECT}
                    icon={<WalletConnectIcon />}
                    name='WalletConnect'
                    onClick={handleWalletConnectSelect}
                />
                <SelectableCard
                    isActive={selectedWallet === WALLET.RABBY}
                    icon={<RabbyIcon />}
                    name='Rabby'
                    onClick={handleRabbySelect}
                />
            </div>
            <Checkbox
                checked={termsAccepted}
                onChange={handleTermsAccept}
                name='terms-and-conditions'
                className='py-5 border-b border-stateDisable mb-5 flex items-center'
            >
                <div className='text-content-primary font-[Inter] text-[14px]'>
                    Accept <a href={PATHS.TERMS_AND_CONDITIONS}>terms of service</a>
                </div>
            </Checkbox>
            <Button
                variant={ButtonVariant.Primary}
                size={ButtonSize.Medium}
                disabled={!selectedWallet || !termsAccepted}
                onClick={handleConnect}
                name={'connect-wallet-submit'}
            >
                Connect Wallet
            </Button>
            <div className='pt-5 text-secondary text-center'>
                Don’t have an account yet?
                <a
                    href={INSTALL_METAMASK_URL}
                    data-testid='learn-how-to-link'
                    className='text-content-accent ml-2 font-semibold'
                >
                    Learn how to connect
                </a>
            </div>
        </div>
    );
};

export const ConnectWalletModal = () => {
    const isNewWalletConnect = useLocalFeatureFlag(Features.NewWalletConnect, true);

    if (!isNewWalletConnect) {
        return <BaseConnectWalletModal />;
    }

    return (
        <Suspense fallback={null}>
            <LazyConnectWalletModal />
        </Suspense>
    );
};
